import React, { useEffect, useMemo, useState } from 'react';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core';
import { IUserProfile, IPasswordPolicy } from './IUserProfile.types';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useLayoutContext } from '../../Context/Layout.context';
import {
  updateUserPassword,
  getPasswordPolicy,
  updateUserPasswordConfirmSms,
} from '../../services/users/users.service';
import { useStyles } from './EditUserPassword.styles';
import { PasswordErrorMessage, BackendErrorCode, FrontendResponseErrorCode, ErrorCode } from './PasswordError.types';
import { AxiosError, AxiosResponse } from 'axios';
import { getPasswordPolicyMessage, validatePasswordWithPolicy } from './helpers';
import _ from 'lodash';
import { generateUUid } from '../../utils/generator';
import { TwoFactorAuthResponse } from '../../services/users/users.types';
import { TokenAuthUser, TokenAuthUserExpired } from './TokenAuthUser.component';
import { Paths } from '../../utils/routerUrls';
import { useHistory } from 'react-router-dom';

interface EditUserPasswordProps {
  user?: IUserProfile;
  userEmail?: string;
  open: boolean;
  onClose?: () => void;
  onNavBack: () => void;
  navButtonLabel?: string;
  title?: string;
}

interface ResponseErrors {
  code: BackendErrorCode;
  descritiption: string;
}

export const EditUserPassword: React.FC<EditUserPasswordProps> = (props) => {
  const classes = useStyles();

  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<ErrorCode[]>([]);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [passwordPolicy, setPasswordPolicy] = useState<IPasswordPolicy | undefined>(undefined);

  const [firstStepModalOn, setFirstStepModalOn] = useState<boolean>(true);
  const [secoundStepModalOn, setSecoundStepModalOn] = useState<boolean>(false);
  const [thirdStepModalOn, setThirdStepModalOn] = useState<boolean>(false);
  const [smsNumber, setSmsNumber] = useState<number | null>(null);
  const [tokenId, setTokenId] = useState<string>('');
  const [leftAttemts, setLeftAttemts] = useState<number | null>(null);
  const [smsExpireDateTime, setSmsExpireDateTime] = useState<Date | null>(null);
  const [smsExpireSeconds, setSmsExpireSeconds] = useState<number | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [token, setToken] = useState<string>('');
  const [prevToken, setPrevToken] = useState<string>('');

  const { openAlert, setIsLoader } = useLayoutContext();
  const history = useHistory();

  const handleClickShowCurrentPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowRepeatedPassword = () => {
    setShowRepeatedPassword(!showRepeatedPassword);
  };

  const handleChangeOldPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(event.target.value);
    setErrorCodes([]);
  };

  const handleChangeNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setErrorCodes([]);
  };

  const handleChangeRepeatedPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatedPassword(event.target.value);
    setErrorCodes([]);
  };

  const clearInputs = () => {
    setOldPassword('');
    setNewPassword('');
    setRepeatedPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowRepeatedPassword(false);
    setErrorCodes([]);
  };

  const handleUpdateUserPassword = () => {
    setErrorCodes([]);
    setIsDataChanged(false);
    if (newPassword !== repeatedPassword) {
      setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordsNotTheSame]));
    } else {
      setIsLoader(true);
      updateUserPassword(oldPassword, newPassword, true)
        .then(
          (result: TwoFactorAuthResponse) => {
            if (!result.twoAuthRequired) {
              openAlert('success');
              setTimeout(() => {
                clearInputs();
                props.onClose && props.onClose();
                props.onNavBack();
              }, 5000);
            } else {
              setSmsNumber(result.smsNumber);
              setLeftAttemts(result.leftAttemts);
              setSmsExpireDateTime(new Date(result.smsExpireDateTime));
              setSmsExpireSeconds(result.smsExpireSeconds);
              setSmsExpireDateTime(new Date(result.smsExpireDateTime));
              setFirstStepModalOn(false);
              setSecoundStepModalOn(true);
              setTokenId(result.tokenId);
              setTimer(result.smsExpireSeconds);
            }
          },
          (error: AxiosError) => {
            if (error.response?.status === 429) {
              localStorage.setItem('2FA', 'failed');
              history.push(Paths.logout);
            }
            if (error.response?.data.errors) {
              openAlert('save_error');
              const validationErrorCodes = error.response?.data.errors.map((error: ResponseErrors) => error.code);
              setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, ...validationErrorCodes]));
            }
          },
        )
        .finally(() => {
          setIsLoader(false);
        });
    }
  };

  const handleConfirmUpdate = () => {
    setIsLoader(true);
    updateUserPasswordConfirmSms(oldPassword, newPassword, token, tokenId)
      .then(
        () => {
          openAlert('success');
          clearInputs();
          props.onClose && props.onClose();
          props.onNavBack();
        },
        (error) => {
          if (error.response.status === 429) {
            localStorage.setItem('2FA', 'failed');
            history.push(Paths.logout);
          }
          setSmsExpireDateTime(new Date(error.response.data.smsExpireDateTime));
          setLeftAttemts(error.response.data.leftAttemts);
          setSmsNumber(error.response.data.smsNumber);
          setPrevToken(token);
          setToken('');
        },
      )
      .finally(() => {
        setIsLoader(false);
      });
  };

  useEffect(() => {
    if (!secoundStepModalOn) return;
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else {
      setThirdStepModalOn(true);
      setSecoundStepModalOn(false);
    }
  }, [timer]);

  useEffect(() => {
    setIsDataChanged(oldPassword !== '' && newPassword !== '' && repeatedPassword !== '');
    if (newPassword !== '' && repeatedPassword !== '' && newPassword !== repeatedPassword) {
      setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordsNotTheSame]));
    }
    if (passwordPolicy && newPassword !== '' && repeatedPassword !== '' && newPassword === repeatedPassword) {
      if (!validatePasswordWithPolicy(newPassword, passwordPolicy)) {
        setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordIsInvalid]));
      }
    }
  }, [oldPassword, newPassword, repeatedPassword, passwordPolicy]);

  useEffect(() => {
    setIsUpdateButtonDisabled(
      oldPassword === '' || newPassword === '' || repeatedPassword === '' || !isDataChanged || errorCodes.length > 0,
    );
  }, [isDataChanged, errorCodes, errorCodes.length]);

  const getErrorMessages = (errorCode: ErrorCode) => {
    switch (errorCode) {
      case BackendErrorCode.PasswordTooShort:
        return PasswordErrorMessage.PasswordTooShort;
      case BackendErrorCode.PasswordRequiresNonAlphanumeric:
        return PasswordErrorMessage.PasswordRequiresNonAlphanumeric;
      case BackendErrorCode.PasswordRequiresDigit:
        return PasswordErrorMessage.PasswordRequiresDigit;
      case BackendErrorCode.PasswordRequiresUpper:
        return PasswordErrorMessage.PasswordRequiresUpper;
      case BackendErrorCode.PasswordRequiresLower:
        return PasswordErrorMessage.PasswordRequiresLower;
      case BackendErrorCode.PasswordTheSame:
        return PasswordErrorMessage.PasswordTheSame;
      case BackendErrorCode.PasswordMismatch:
        return PasswordErrorMessage.PasswordMismatch;
      case FrontendResponseErrorCode.PasswordsNotTheSame:
        return PasswordErrorMessage.PasswordsNotTheSame;
      case FrontendResponseErrorCode.PasswordIsInvalid:
        return PasswordErrorMessage.PasswordIsInvalid;
    }
  };

  const onCancelEdit = () => {
    clearInputs();
    props.onClose && props.onClose();
    props.onNavBack();
  };

  const newPasswordsMatchButOldIsInvalid =
    !errorCodes.includes(FrontendResponseErrorCode.PasswordsNotTheSame) &&
    errorCodes.includes(BackendErrorCode.PasswordMismatch);
  const oldPasswordIsValidButThereAreErrors =
    !errorCodes.includes(BackendErrorCode.PasswordMismatch) && errorCodes.length > 0;

  useEffect(() => {
    getPasswordPolicy().then((response: AxiosResponse<IPasswordPolicy>) => {
      setPasswordPolicy(response?.data);
    });
  }, []);

  const oldPasswordId = useMemo(() => {
    return generateUUid('');
  }, []);

  return (
    <>
      <ModalWithButtons
        open={props.open && firstStepModalOn}
        size="sm"
        navButtonLabel={props.navButtonLabel ?? 'Mój profil'}
        handleNavBack={onCancelEdit}
        xCloseButton={props.onClose ? onCancelEdit : undefined}
        autoHeight={true}
        onCancel={onCancelEdit}
        onConfirm={handleUpdateUserPassword}
        confirmButtonLabel="Aktualizuj"
        confirmButtonDisabled={isUpdateButtonDisabled}
        buttonsContainerStyle={{ marginTop: '20px' }}
        annotations={
          <>
            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.footer}>{passwordPolicy && getPasswordPolicyMessage(passwordPolicy)}</div>
            </Grid>
          </>
        }
      >
        <Grid container className={classes.container}>
          <Grid>
            <div className={classes.modalSubTitle} style={{ marginTop: props.title ? '30px' : '' }}>
              {props.title ? props.title : 'Edytuj hasło'}
            </div>
          </Grid>
          <Grid item xs={7} className={classes.item}>
            <div className={classes.emailHeader}>
              Email: <div className={classes.email}> {props.user ? props.user?.email : props?.userEmail}</div>
            </div>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{ color: newPasswordsMatchButOldIsInvalid ? '#BC0000' : '#2C2D3E' }}
                htmlFor={oldPasswordId}
              >
                Aktualne hasło
              </InputLabel>
              <OutlinedInput
                id={oldPasswordId}
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={handleChangeOldPassword}
                fullWidth={true}
                error={newPasswordsMatchButOldIsInvalid}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowCurrentPassword}
                      edge="end"
                    >
                      {!showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: newPasswordsMatchButOldIsInvalid ? '#BC0000' : '#2C2D3E',
                  },
                  autocomplete: 'new-password', // this is not the field for new password, but autocomplete = 'new-password' prevent from autofilling the field
                }}
                label="Aktualne hasło"
              />
              <FormHelperText
                className={classes.oldPasswordErrorMessage}
                error
                id="old-password-error"
                component={'div'}
              >
                {errorCodes.includes(BackendErrorCode.PasswordMismatch) && (
                  <div className={classes.errorMessage}> {PasswordErrorMessage.PasswordMismatch} </div>
                )}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="new-password"
              >
                Nowe hasło
              </InputLabel>
              <OutlinedInput
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleChangeNewPassword}
                fullWidth={true}
                error={oldPasswordIsValidButThereAreErrors}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowNewPassword}
                      edge="end"
                    >
                      {!showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Nowe hasło"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="repeted-password"
              >
                Powtórz nowe hasło
              </InputLabel>
              <OutlinedInput
                id="repeted-password"
                type={showRepeatedPassword ? 'text' : 'password'}
                value={repeatedPassword}
                onChange={handleChangeRepeatedPassword}
                fullWidth={true}
                error={oldPasswordIsValidButThereAreErrors}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowRepeatedPassword}
                      edge="end"
                    >
                      {!showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: oldPasswordIsValidButThereAreErrors ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Powtórz nowe hasło"
              />
              <FormHelperText className={classes.errorMessagesContainer} error id="password-error" component={'div'}>
                {errorCodes.includes(FrontendResponseErrorCode.PasswordsNotTheSame) ? (
                  <div className={classes.errorMessage}>
                    {getErrorMessages(FrontendResponseErrorCode.PasswordsNotTheSame)}
                  </div>
                ) : (
                  <>
                    {!errorCodes.includes(BackendErrorCode.PasswordMismatch) &&
                      errorCodes.map((code: ErrorCode) => (
                        <div className={classes.errorMessage} key={code}>
                          {getErrorMessages(code)}
                        </div>
                      ))}
                  </>
                )}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </ModalWithButtons>

      <TokenAuthUser
        open={props.open && secoundStepModalOn}
        token={token}
        prevToken={prevToken}
        smsNumber={smsNumber}
        timer={timer}
        leftAttemts={leftAttemts}
        smsExpireDateTime={smsExpireDateTime}
        setToken={setToken}
        onCancel={() => {
          clearInputs();
          props.onClose && props.onClose();
          props.onNavBack();
        }}
        handleConfirmUpdate={handleConfirmUpdate}
      />

      <TokenAuthUserExpired
        open={props.open && thirdStepModalOn}
        onCancel={() => {
          clearInputs();
          props.onClose && props.onClose();
          props.onNavBack();
        }}
        onConfirm={() => {
          handleUpdateUserPassword();
          setThirdStepModalOn(false);
          setSecoundStepModalOn(true);
        }}
      />
    </>
  );
};
